import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MessageSquare, Paperclip } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { TagDisplay } from '@/components/tasks/tag-picker';
import { TaskAssigneesCell } from '@/components/tasks/assignees-display';
import { cn, formatDate, getPriorityHexColor, getContrastTextColor } from '@/lib/utils';
import type { Task } from '@/types';

interface TaskCardProps {
  task: Task;
  isDragging?: boolean;
}

export const TaskCard = memo(function TaskCard({ task, isDragging }: TaskCardProps) {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isDragging) {
      navigate(`/tasks/${task.id}`);
    }
  };

  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && !task.completedAt;

  return (
    <div
      onClick={handleClick}
      className={cn(
        'cursor-pointer rounded-lg border bg-card p-3 shadow-sm transition-all hover:border-primary/50 hover:shadow-md',
        isDragging && 'rotate-3 shadow-lg ring-2 ring-primary'
      )}
    >
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-medium leading-tight line-clamp-2">{task.title}</h4>
          {(() => {
            const bgColor = getPriorityHexColor(task.priority);
            const textColor = getContrastTextColor(bgColor);
            return (
              <Badge
                variant="secondary"
                className="shrink-0 text-[10px] px-1.5 capitalize font-medium"
                style={{ backgroundColor: bgColor, color: textColor, borderColor: 'transparent' }}
              >
                {task.priority}
              </Badge>
            );
          })()}
        </div>

        {task.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">{task.description}</p>
        )}

        {task.tags && task.tags.length > 0 && (
          <TagDisplay tags={task.tags} maxDisplay={3} />
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {task.dueDate && (
              <span className={cn('flex items-center gap-1', isOverdue && 'text-destructive')}>
                <Calendar className="h-3 w-3" />
                {formatDate(task.dueDate)}
              </span>
            )}

            {task._count && task._count.comments > 0 && (
              <span className="flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                {task._count.comments}
              </span>
            )}

            {task._count && task._count.attachments > 0 && (
              <span className="flex items-center gap-1">
                <Paperclip className="h-3 w-3" />
                {task._count.attachments}
              </span>
            )}
          </div>

          <div className="shrink-0 flex justify-end">
            <TaskAssigneesCell task={task} variant="compact" mode="avatars-only" />
          </div>
        </div>
      </div>
    </div>
  );
});
